<template>
  <v-dialog v-model="isOpen" max-width="600" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-camera</v-icon>
        Take Selfie for Verification
        <v-spacer></v-spacer>
        <v-btn icon @click="closeCamera">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Camera View -->
        <div v-if="!cameraError" class="camera-container">
          <video
            ref="videoElement"
            autoplay
            playsinline
            class="camera-preview"
            :class="{ mirror: !isCaptured }"
          ></video>

          <!-- Camera Controls -->
          <div v-if="!isCaptured" class="camera-controls">
            <v-btn
              color="primary"
              fab
              large
              @click="capturePhoto"
              :disabled="!isStreamActive"
              class="capture-btn"
            >
              <v-icon>mdi-camera</v-icon>
            </v-btn>
          </div>

          <!-- Captured Photo Preview -->
          <div v-if="isCaptured" class="captured-photo">
            <img
              :src="capturedImageData"
              alt="Captured photo"
              class="photo-preview"
            />
            <div class="photo-actions">
              <v-btn color="error" @click="retakePhoto" class="mr-2">
                <v-icon left>mdi-refresh</v-icon>
                Retake
              </v-btn>
              <v-btn
                color="success"
                @click="confirmPhoto"
                :loading="isUploading"
              >
                <v-icon left>mdi-check</v-icon>
                Confirm
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-if="cameraError" class="error-state text-center pa-4">
          <v-icon color="error" size="64" class="mb-4">mdi-camera-off</v-icon>
          <h3 class="mb-2">Camera Access Required</h3>
          <p class="mb-4">{{ cameraError }}</p>
          <v-btn color="primary" @click="requestCameraAccess">
            <v-icon left>mdi-camera</v-icon>
            Allow Camera Access
          </v-btn>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state text-center pa-4">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            class="mb-4"
          ></v-progress-circular>
          <p>Initializing camera...</p>
        </div>
      </v-card-text>

      <v-card-actions v-if="!isCaptured && !cameraError">
        <v-spacer></v-spacer>
        <v-btn text @click="closeCamera">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import kycApi from '../api/users/kyc'

export default {
  name: 'CameraCapture',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      videoElement: null,
      stream: null,
      isStreamActive: false,
      isCaptured: false,
      capturedImageData: null,
      cameraError: null,
      isLoading: false,
      isUploading: false
    }
  },
  watch: {
    open(newVal) {
      this.isOpen = newVal
      if (newVal) {
        this.initializeCamera()
      } else {
        this.stopCamera()
      }
    },
    isOpen(newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  methods: {
    async initializeCamera() {
      this.isLoading = true
      this.cameraError = null
      this.isCaptured = false
      this.capturedImageData = null

      try {
        // Request camera access
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user', // Front camera for selfie
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        })

        this.videoElement = this.$refs.videoElement
        this.videoElement.srcObject = this.stream
        this.isStreamActive = true
        this.isLoading = false

        // Wait for video to be ready
        this.videoElement.onloadedmetadata = () => {
          this.videoElement.play()
        }
      } catch (error) {
        this.isLoading = false
        this.handleCameraError(error)
      }
    },

    handleCameraError(error) {
      console.error('Camera error:', error)

      if (error.name === 'NotAllowedError') {
        this.cameraError =
          'Camera access was denied. Please allow camera access and try again.'
      } else if (error.name === 'NotFoundError') {
        this.cameraError = 'No camera found on this device.'
      } else if (error.name === 'NotSupportedError') {
        this.cameraError = 'Camera is not supported on this device.'
      } else if (error.name === 'NotReadableError') {
        this.cameraError = 'Camera is already in use by another application.'
      } else {
        this.cameraError =
          'Unable to access camera. Please check your device settings.'
      }
    },

    async requestCameraAccess() {
      this.initializeCamera()
    },

    capturePhoto() {
      if (!this.videoElement || !this.isStreamActive) return

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      // Set canvas dimensions to match video
      canvas.width = this.videoElement.videoWidth
      canvas.height = this.videoElement.videoHeight

      // Draw the current video frame to canvas
      context.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height)

      // Convert to data URL
      this.capturedImageData = canvas.toDataURL('image/jpeg', 0.8)
      this.isCaptured = true

      // Stop the camera stream
      this.stopCamera()
    },

    retakePhoto() {
      this.isCaptured = false
      this.capturedImageData = null
      this.initializeCamera()
    },

    async confirmPhoto() {
      if (!this.capturedImageData) return

      this.isUploading = true

      try {
        // Convert data URL to blob
        const response = await fetch(this.capturedImageData)
        const blob = await response.blob()

        // Create FormData for API submission
        const formData = new FormData()
        formData.append('image', blob, 'selfie.jpg')
        formData.append('type', 'liveness_check')

        // Submit to KYC API
        const result = await kycApi.kyc().livenessCheck(formData)

        if (result.error) {
          this.$store.dispatch('alert', {
            message:
              result.message || 'Failed to upload photo. Please try again.',
            error: true,
            status: true
          })
        } else {
          this.$store.dispatch('alert', {
            message:
              'Photo uploaded successfully! Verification is being processed.',
            error: false,
            status: true
          })
          this.$emit('photo-uploaded', result.data)
          this.closeCamera()
        }
      } catch (error) {
        console.error('Upload error:', error)
        this.$store.dispatch('alert', {
          message: 'Failed to upload photo. Please try again.',
          error: true,
          status: true
        })
      } finally {
        this.isUploading = false
      }
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop())
        this.stream = null
      }
      this.isStreamActive = false
    },

    closeCamera() {
      this.stopCamera()
      this.isOpen = false
      this.$emit('close')
    }
  },

  beforeDestroy() {
    this.stopCamera()
  }
}
</script>

<style scoped>
.camera-container {
  position: relative;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.camera-preview {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.camera-preview.mirror {
  transform: scaleX(-1);
}

.camera-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
}

.capture-btn {
  width: 80px !important;
  height: 80px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.captured-photo {
  width: 100%;
  text-align: center;
}

.photo-preview {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.photo-actions {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.error-state,
.loading-state {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.error-state h3 {
  color: #f44336;
}

.loading-state p {
  color: #666;
}
</style>
